import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Chip, IconButton, MenuItem } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import FormTextField from '../../../Components/Form/FormTextField';
import FormSelect from '../../../Components/Form/FormSelect';
import FormCheckbox from '../../../Components/Form/FormCheckbox';
import { PWD_DEFAULT } from '../Utils/constants';
import validateEmail from '../../../Helpers/Validation/validateEmail';
import BranchesAccordion from './BranchesAccordion';

const useStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    gap: '8px',
  },
});
const { EMAIL_DOMAIN } = window.databeasy || {};

const generateDefaultUser = (name, surname) =>
  name && surname ? `${name[0]?.toLowerCase()}.${surname?.toLowerCase()}` : '';
const generateDefaultEmail = (name, surname) =>
  name && surname
    ? `${name?.toLowerCase()}.${surname?.toLowerCase()}@${EMAIL_DOMAIN}`
    : '';

function RoleCustomItem(props) {
  const intl = useIntl();
  const { licences, activeUsers, children, onClick } = props || {};

  const chipStyle = () => {
    if (licences === null || licences - activeUsers <= 0) return {};

    const percentage = parseInt((activeUsers / licences) * 100, 10);

    if (percentage > 50) return { color: '#7a451f', bgcolor: '#ffd2a8' };
    return { color: '#298073', bgcolor: '#bee5e1' };
  };

  return (
    <MenuItem
      value={children}
      onClick={onClick}
      disabled={licences !== null && licences - activeUsers <= 0}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {children}
        <Chip
          size="small"
          label={
            licences === null
              ? intl.messages.LIMITLESS
              : `${activeUsers || 0} / ${licences || 0}`
          }
          sx={{
            ...chipStyle(),
            minWidth: '64px',
          }}
        />
      </div>
    </MenuItem>
  );
}

const UserDataForm = function UserDataForm({
  errors = {},
  edit = false,
  rolesList = [],
  allBranches = [],
  userBranches = [],
}) {
  const intl = useIntl();
  const classes = useStyles();
  const [viewPwd, setViewPwd] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [defaultBranch, setDefaultBranch] = useState(null);
  const {
    watch,
    setValue,
    getValues,
    setError,
    clearErrors,
    getFieldState,
    formState: { isSubmitting },
  } = useFormContext();
  //watch renders the component when one of the values of the provided fields changes
  const [name, surname] = watch(['name', 'surname']);

  //shows/hide password
  const togglePwd = () => {
    setViewPwd(!viewPwd);
  };

  /**Selected branches functions**/
  const handleAddBranchToUser = ({ id, branchDescription }) => {
    if (!selectedBranches.find((b) => b.id === id))
      setSelectedBranches((oldAr) => [...oldAr, { id, branchDescription }]);
  };
  const handleRemoveBranchToUser = (branchId) => {
    setSelectedBranches((oldAr) => oldAr.filter(({ id }) => id !== branchId));
  };
  const handleChangeDefaultBranch = (branchId) => {
    setDefaultBranch(branchId);
  };
  /****/

  useEffect(() => {
    if (isSubmitting && getValues('branches')?.length === 0) {
      setError('branches', {
        type: 'custom',
        message: intl.messages.AT_LEAST_ONE_BRANCH,
      });
    }
  }, [isSubmitting]);

  useEffect(() => {
    setValue(
      'branches',
      selectedBranches.map(({ id }) => ({
        id,
        isDefault: defaultBranch === id,
      }))
    );
    clearErrors('branches');
  }, [selectedBranches, defaultBranch]);

  useEffect(() => {
    if (
      !edit ||
      getFieldState('name')?.isDirty ||
      getFieldState('surname')?.isDirty
    ) {
      setValue('userName', generateDefaultUser(name, surname));
      setValue('email', generateDefaultEmail(name, surname));
      clearErrors(['userName', 'email']); // remove errors manually
    }
  }, [name, surname]);

  useEffect(() => {
    if (selectedBranches.length < 1) {
      setDefaultBranch(null);
    } else if (!selectedBranches.find(({ id }) => defaultBranch === id)) {
      setDefaultBranch(selectedBranches[0]?.id);
    }
  }, [selectedBranches]);

  useEffect(() => {
    if (userBranches && userBranches.length > 0) {
      //only in edit
      setSelectedBranches(
        userBranches.map(({ id, branchDescription }) => ({
          id,
          branchDescription,
        }))
      );
      setDefaultBranch(userBranches.find(({ isDefault }) => isDefault)?.id);
    }
  }, [userBranches]);

  return (
    <>
      <div style={classes.root}>
        <FormTextField allUpperCase name="name" error={errors?.name} required />
        <FormTextField
          allUpperCase
          name="surname"
          error={errors?.surname}
          required
        />

        <FormTextField
          name="email"
          error={errors?.email}
          required
          validateCallback={(emailValue) =>
            validateEmail(emailValue) || intl.messages.INVALID_EMAIL_ADDRESS
          }
        />
        <FormTextField
          name="extension"
          error={errors?.extension}
          validateCallback={(extension) =>
            extension === '' ||
            extension === null ||
            extension === undefined ||
            extension?.length > 1 ||
            extension?.length <= 3
              ? true
              : intl.messages.INVALID_EXTENSION
          }
        />
        <FormTextField
          name="phoneNumber"
          error={errors?.phoneNumber}
          validateCallback={(phoneNumber) =>
            !isNaN(phoneNumber) && phoneNumber !== '+39'
              ? true
              : intl.messages.INVALID_PHONE_NUMBER
          }
        />
        <div>
          <FormTextField
            required={!edit}
            type={viewPwd ? 'text' : 'password'}
            name="password"
            error={errors?.password}
            defaultValue={edit ? null : PWD_DEFAULT}
            icon={
              <IconButton onClick={togglePwd} edge="end">
                {viewPwd ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            }
          />
          <div style={{ marginLeft: '8px' }}>
            <FormCheckbox
              name="passwordExpiration"
              error={errors?.passwordExpiration}
            />
          </div>
        </div>
      </div>
      <div style={classes.root}>
        <FormTextField name="userName" error={errors?.userName} required />
        <FormSelect
          required
          name="roleId"
          error={errors?.roleId}
          options={rolesList}
          noTranslation
          CustomItem={RoleCustomItem}
        />
        <FormTextField
          name="extensionData.target"
          defaultValue={!edit ? 35 : undefined}
          label={intl.messages.CALLS_TARGET}
          error={errors?.target}
          type="number"
        />
        <BranchesAccordion
          error={errors?.branches}
          allBranches={allBranches}
          defaultBranch={defaultBranch}
          selectedBranches={selectedBranches}
          handleAddBranchToUser={handleAddBranchToUser}
          handleRemoveBranchToUser={handleRemoveBranchToUser}
          handleChangeDefaultBranch={handleChangeDefaultBranch}
        />
      </div>
    </>
  );
};

UserDataForm.propTypes = {
  errors: PropTypes.object,
  edit: PropTypes.bool,
  rolesList: PropTypes.array,
  allBranches: PropTypes.array,
  userBranches: PropTypes.array,
};

export default UserDataForm;
